import { ChangeEvent, useEffect, useState, useReducer } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { unwrapResult } from "@reduxjs/toolkit";
import { SelectChangeEvent } from "@mui/material";

import {
  CustomAlert,
  CustomButton,
  CustomInput,
  CustomSelect,
  Text,
} from "../../../../../../../shared/uiComponents";
import {
  errorNormalizer,
  validateNumber,
} from "../../../../../../../shared/Helpers/functions";
import { initialState } from "./initialState";
import { ACTIONS, TargetReducer } from "./reducer";

import {
  DispatchProperties,
  useSelector,
} from "../../../../../../../redux/store";
import {
  getMasteryCriteria,
  getTargets,
  updateTarget,
} from "../../../../../../../redux/State/clientSlice/programTargetSlice";
import {
  MasteryTypes,
  TargetProperties,
} from "../../../../../../../redux/API/ClientAPIHelpers/programTargetsProperties";

const Edit = ({
  item,
  programId,
}: {
  item: TargetProperties;
  programId: string;
}) => {
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const { id } = item;

  return (
    <>
      <CustomButton title="Edit Details" onClick={() => setOpenEdit(true)} />
      <CustomAlert
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        Content={() => (
          <UpdateTargetContent
            closeHandler={() => setOpenEdit(false)}
            id={id}
            programId={programId}
            props={item}
          />
        )}
      />
    </>
  );
};

export const UpdateTargetContent = ({
  closeHandler,
  props,
  id,
  programId,
}: {
  closeHandler: () => void;
  props: TargetProperties;
  id: string;
  programId: string;
}) => {
  const dispatch = useDispatch<DispatchProperties>();

  const loading = useSelector((state) => state.programTarget.loading);
  const masteryOptions = useSelector(
    (state) => state.programTarget.masteryCriterias
  );

  const [targetData, setTargetData] = useReducer(TargetReducer, initialState);
  const {
    name,
    goalName,
    errorCorrection,
    reinforcement,
    sd,
    targetInstructions,
    masteryCriteria,
    consecutiveDays,
    responsePercentage,
  } = targetData;

  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!props) return;
    const { masteryCriteria, consecutiveDays } = props;
    setTargetData({ type: ACTIONS.setState, payload: props });
    setTargetData({
      type: ACTIONS.setMasteryCriteria,
      payload: masteryCriteria.id,
    });
    if (consecutiveDays === 0) {
      setTargetData({ type: ACTIONS.setConsecutiveDays, payload: null });
    }
  }, [props]);

  useEffect(() => {
    if (!!name) {
      setError("");
      return;
    }

    setError("Required");
  }, [name]);

  useEffect(() => {
    if (!!masteryOptions.length) return;
    dispatch(getMasteryCriteria());
  }, [dispatch, masteryOptions]);

  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTargetData({ type: ACTIONS.setName, payload: value });
  };

  const onGoalNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTargetData({ type: ACTIONS.setGoalName, payload: value });
  };

  const onSdChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTargetData({ type: ACTIONS.setSD, payload: value });
  };

  const onTargetInstructionsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTargetData({ type: ACTIONS.setTargetInstructions, payload: value });
  };

  const onErrorCorrectionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTargetData({ type: ACTIONS.setErrorCorrection, payload: value });
  };

  const onReinforcementChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTargetData({ type: ACTIONS.setReinforcement, payload: value });
  };

  const updateTagetHandler = () => {
    if (!programId || !id) return;
    dispatch(updateTarget({ data: targetData, programId, id }))
      .then(unwrapResult)
      .then(() => {
        dispatch(getTargets(programId));
        closeHandler();
        toast("Updated");
      })
      .catch(errorNormalizer);
  };

  const onMasteryCriteriaChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    const payload = parseInt(value);
    setTargetData({ type: ACTIONS.setMasteryCriteria, payload });
    if (MasteryTypes.AUTOMATIC === payload) {
      setTargetData({ type: ACTIONS.setResponsePercentage, payload: 80 });
    }
    if (MasteryTypes.MANUAL === payload) {
      setTargetData({ type: ACTIONS.setConsecutiveDays, payload: null });
      setTargetData({ type: ACTIONS.setResponsePercentage, payload: null });
    }
  };

  const onConsecutiveDaysChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!validateNumber(value)) return;
    setTargetData({
      type: ACTIONS.setConsecutiveDays,
      payload: value,
    });
  };

  const onResponsePercentageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!validateNumber(value)) return;
    setTargetData({
      type: ACTIONS.setResponsePercentage,
      payload: value,
    });
  };

  return (
    <div>
      <Text
        title="Target Information"
        size="mediumBold"
        className="marginBottom16"
      />
      <CustomInput
        label="Target Name"
        className="marginBottom16"
        value={name}
        setValue={onNameChange}
        error={!name}
        errorMessage={error}
      />
      <CustomInput
        label="SD"
        placeholder="Required"
        className="marginBottom16"
        value={sd}
        setValue={onSdChange}
      />
      <CustomSelect
        label="Mastery Criteria"
        className="marginBottom16"
        value={masteryCriteria.toString()}
        setValue={onMasteryCriteriaChange}
        data={masteryOptions}
      />
      {masteryCriteria === MasteryTypes.AUTOMATIC && (
        <>
          <CustomInput
            label="Consecutive Days"
            className="marginBottom16"
            value={consecutiveDays}
            setValue={onConsecutiveDaysChange}
          />
          <CustomInput
            label="Response Percentage"
            className="marginBottom16"
            value={responsePercentage}
            setValue={onResponsePercentageChange}
          />
        </>
      )}
      <CustomInput
        label="Goal Name (for reporting)"
        className="marginBottom16"
        value={goalName}
        setValue={onGoalNameChange}
        multiline={{
          multiline: true,
          rowCount: 4,
        }}
      />
      <CustomInput
        label="Target Instructions"
        placeholder="Required"
        className="marginBottom16"
        value={targetInstructions}
        setValue={onTargetInstructionsChange}
        multiline={{
          multiline: true,
          rowCount: 4,
        }}
      />
      <CustomInput
        placeholder="Required"
        label="Error Correction"
        className="marginBottom16"
        value={errorCorrection}
        setValue={onErrorCorrectionChange}
        multiline={{
          multiline: true,
          rowCount: 4,
        }}
      />
      <CustomInput
        placeholder="Required"
        label="Reinforcement"
        className="marginBottom16"
        value={reinforcement}
        setValue={onReinforcementChange}
        multiline={{
          multiline: true,
          rowCount: 4,
        }}
      />
      <CustomButton
        title="Save"
        onClick={updateTagetHandler}
        loading={loading}
        disabled={loading || !!error}
      />
    </div>
  );
};

export default Edit;
